import _sortBy from 'lodash.sortby';
import Service, { inject as service } from '@ember/service';
import { set, action } from '@ember/object';
import { alias } from '@ember/object/computed';
import DS from 'ember-data';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from './environment';
import FeaturesService from 'ember-feature-flags/services/features';
import SubscriptionPageModel from 'cio/models/subscription-page';
import SubscriptionTopicModel from 'cio/models/subscription-topic';
import { all, dropTask, enqueueTask, task } from 'ember-concurrency';
import AnalyticsService from './analytics';
import { tracked } from '@glimmer/tracking';
import config from 'cio/config/environment';
import RavenService from 'cio/services/raven';
import SubscriptionLanguageModel from 'cio/models/subscription-language';
import ApiService from 'cio/services/api';
import { isEmpty } from '@ember/utils';
import pluralize from 'cio/utils/pluralize';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import { SendToUnsubscribedValues } from 'cio/utils/subscription-center';

export type CampaignBroadcastStatLinkData = {
  name: string;
  count: number;
  route: string;
  query?: object;
};

export enum MissingTopicType {
  Campaign = 'running campaign',
  ScheduledBroadcast = 'scheduled newsletter',
  ApiTriggeredBroadcast = 'API triggered broadcast',
}

export enum SubscriptionCenterPages {
  Preferences = 'sub-preferences-page',
}

export type MessageData = {
  id: number;
  uniqueID: string;
  name: string;
  type: string;
  created: number;
  updated: number;
  subscriptionTopicId: number;
  sendToUnsubscribed: boolean;
  description: string;
  pendingActions: PendingActionData[];
};

type PendingActionData = {
  id: number;
  name: string;
};

export type MessageListData = {
  messageList?: MessageData[];
  pagination: {
    page: number;
    size: number;
    total: number;
  };
};

export type BulkPreferenceUpdateParams = {
  campaignIDs: number[];
  newsletterIDs: number[];
  sendToUnsub?: boolean | null;
  topicID?: number;
  preferenceName: string;
};

export interface MessageListFilterParams {
  searchStr?: string | null;
  tagIds?: string[] | null;
  type?: string | null;
  topicId?: string | null;
  sendToUnsubs?: SendToUnsubscribedValues | null;
  page?: number | null;
}

export default class SubscriptionCenterService extends Service {
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service store!: DS.Store;
  @service raven!: RavenService;
  @service api!: ApiService;

  @tracked initialized = false;
  @tracked allTopics: SubscriptionTopicModel[] = [];
  @tracked allLanguages: SubscriptionLanguageModel[] = [];
  @tracked pendingMessages: MessageListData = { pagination: { page: 0, size: 0, total: 0 } };
  @tracked completedMessages: MessageListData = { pagination: { page: 0, size: 0, total: 0 } };
  @tracked allMessages: MessageListData = { pagination: { page: 0, size: 0, total: 0 } };
  @tracked pages: SubscriptionPageModel[] = [];
  @tracked preferencesPage: SubscriptionPageModel | null = null;
  @tracked entitiesWithoutTopics: Record<'campaigns' | 'newsletters' | 'actions', number> = {
    campaigns: 0,
    newsletters: 0,
    actions: 0,
  };

  @alias('environment.current.subscriptionCenterEnabled') enabled!: boolean;

  get hasEntitiesWithoutTopics() {
    let { campaigns, newsletters, actions } = this.entitiesWithoutTopics;
    return [campaigns, newsletters, actions].some((count) => count > 0);
  }

  get canBeEnabled() {
    if (!this.initialized) {
      return false;
    }

    return !this.hasEntitiesWithoutTopics && !this.hasNoTopics;
  }

  get topics() {
    if (!this.initialized) {
      // We don't want to break the page for our users if this error occurs, so
      // instead let's report to sentry for investigation and throw in non-prod
      // environments
      let err = new Error(
        'Tried to access topics before initializing service. The subscription center service should have `initialized` set to true before accessing `topics`.',
      );

      if (config.isProduction) {
        this.raven.captureException(err);
      } else {
        throw err;
      }
    }

    return this.allTopics;
  }

  get topicOrder() {
    if (!this.initialized) {
      return [];
    }

    return this.topics.map((topic) => topic.name);
  }

  get optOutTopics(): SubscriptionTopicModel[] {
    return this.topics.filter((topic) => !topic.isOptIn);
  }

  get optInTopics(): SubscriptionTopicModel[] {
    return this.topics.filter((topic) => topic.isOptIn);
  }

  get hasNoTopics() {
    if (!this.initialized) {
      return true;
    }

    return this.topics.length === 0;
  }

  get enabledWithLastTopic() {
    return this.topics.length < 2 && this.enabled;
  }

  get languages() {
    if (!this.initialized) {
      // We don't want to break the page for our users if this error occurs, so
      // instead let's report to sentry for investigation and throw in non-prod
      // environments
      let err = new Error(
        'Tried to access languages before initializing service. The subscription language service should have `initialized` set to true before accessing `languages`.',
      );

      if (config.isProduction) {
        this.raven.captureException(err);
      } else {
        throw err;
      }
    }

    return this.allLanguages;
  }

  get allLanguageCodes() {
    return this.allLanguages.map((language) => language.id.toLowerCase());
  }

  get topicsMissingTranslations() {
    return [
      ...new Set(
        this.languages.reduce((topicIds, language) => {
          if (language.isTopicsMissingContent && language.enabled) {
            return [...topicIds, ...language.missingTopicIds];
          }
          return topicIds;
        }, []),
      ),
    ];
  }

  // This is enqueued to allow multiple components/controllers/services to call
  // initialize. Because this returns early if the service has been initialized,
  // queued tasks should almost immediately resolve. This avoids the problem
  // with a @dropTask, where duplicate/subsequent task performances are
  // cancelled while a task is running, rather than being attached to the
  // running task.
  initializeTask = enqueueTask(async (forceRefresh = false) => {
    if (forceRefresh === false && this.initialized) {
      return;
    }

    await Promise.all([this.fetchTopics(), this.fetchPages(), this.fetchLanguages(true)]);
    this.initialized = true;
  });

  async fetchPages() {
    this.pages = await this.store
      .findAll('subscription-page')
      .then((r) => r.toArray())
      .catch((e) => {
        this.apiErrors.report(e);
        return [];
      });
    this.preferencesPage = this.pages.find((item) => item.id === SubscriptionCenterPages.Preferences) ?? null;
  }

  async fetchTopics() {
    let topics = await this.store.query('subscription-topic', {});
    this.allTopics = topics.toArray();
    return topics;
  }

  async fetchLanguages(refresh = false) {
    try {
      let api: keyof DS.Store = refresh ? 'findAll' : 'peekAll';
      let languages = await this.store[api]('subscription-language');

      this.allLanguages = _sortBy(languages.toArray(), ['id']);
    } catch (err) {
      this.apiErrors.report(err);
    }
  }

  fetchPendingMessageLists = dropTask(async (params: MessageListFilterParams) => {
    try {
      let response = await this.filterMessages(params, 'pending');
      this.pendingMessages = this.processMessageList(response);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  fetchCompletedMessageLists = dropTask(async (params: MessageListFilterParams) => {
    try {
      let response = await this.filterMessages(params, 'completed');
      this.completedMessages = this.processMessageList(response);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  fetchAllMessageLists = dropTask(async (params: MessageListFilterParams) => {
    try {
      let response = await this.filterMessages(params);
      this.allMessages = this.processMessageList(response);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  filterMessages({ type, page, tagIds, searchStr, topicId, sendToUnsubs }: MessageListFilterParams, state?: string) {
    page = page ?? 1;
    let url = `subscription_topic_messages/list`;
    let filters = [];
    let queries = [];

    if (!isEmpty(state)) {
      queries.push(`state=${state}`);
    }
    if (!isEmpty(type)) {
      queries.push(`type=${type}`);
      filters.push('Type');
    }
    if (!isEmpty(tagIds)) {
      tagIds?.forEach((id) => {
        queries.push(`tagIds[]=${id}`);
      });
      filters.push('Tags');
    }
    if (!isEmpty(searchStr)) {
      queries.push(`search=${encodeURIComponent(searchStr as string)}`);
      filters.push('Search');
    }
    if (!isEmpty(topicId)) {
      queries.push(`topicId=${topicId}`);
      filters.push('Subscription preference');
    }
    if (!isEmpty(sendToUnsubs)) {
      queries.push(`globalPreference=${sendToUnsubs}`);
      filters.push('Subscription preference');
    }

    queries.push(`page=${page - 1}`);
    url += `?${queries.join('&')}`;

    if (filters.length > 0) {
      this.analytics.track('subscription_center', 'set_bulk_preferences_filtered', {
        filters: filters.join(', '),
      });
    }

    return this.api.request(url);
  }

  processMessageList(response: Record<string, any>) {
    let { message_list, meta, ...storePayload } = response;
    let messageList: MessageData[] = [];

    message_list?.forEach((message: Record<string, any>) => {
      let { pending_actions } = message;

      messageList.push({
        id: message.id,
        uniqueID: `${message.type}-${message.id}`,
        name: message.name,
        type: message.type,
        created: message.created,
        updated: message.updated,
        subscriptionTopicId: message.subscription_topic_id,
        sendToUnsubscribed: message.send_to_unsubscribed,
        description: message.description,
        pendingActions: pending_actions ?? [],
      });
    });

    let messageListData: MessageListData = {
      messageList,
      pagination: {
        total: meta.pagination.total,
        page: meta.pagination.page,
        size: meta.pagination.size,
      },
    };

    delete response.message_list;
    delete response.meta;
    this.store.pushPayload(storePayload);
    return messageListData;
  }

  fetchMissingTopicsTask = dropTask(async () => {
    if (this.enabled) {
      return;
    }

    let { campaigns, newsletters, actions } = await this.api.request('subscription_topic_messages/list?state=pending');
    campaigns = campaigns.length;
    newsletters = newsletters.length;
    actions = actions.length;
    this.entitiesWithoutTopics = { campaigns, newsletters, actions };
  });

  async enable() {
    set(this.environment.current, 'subscriptionCenterEnabled', true);
    try {
      await this.environment.current.save();
    } catch (e) {
      this.environment.current.rollbackAttributes();
      this.apiErrors.report(e);
      return;
    }

    this.analytics.track('subscription_center', 'activation_completed');
  }

  async disable() {
    set(this.environment.current, 'subscriptionCenterEnabled', false);
    await this.environment.current.save();
    this.analytics.track('subscription_center', 'deactivation_completed');

    this.alerts.success('subscriptionCenter.disabled', `Subscription center is now disabled`);
  }

  @action
  async deleteTopic(topic: SubscriptionTopicModel) {
    try {
      await topic.destroyRecord();

      this.alerts.success('subscriptionCenter.deleteTopic.success', 'Topic deleted');
      this.allTopics = this.topics.filter((t) => t !== topic);
    } catch (e) {
      topic.rollbackAttributes();
      this.apiErrors.report(e);
    }
  }

  @action
  async updateTopic(topic: SubscriptionTopicModel) {
    try {
      await topic.save();

      this.alerts.success('subscriptionCenter.editTopic.success', 'Topic updated');
    } catch (e) {
      topic.rollbackAttributes();
      this.apiErrors.report(e);
    }
  }

  createPage() {
    this.preferencesPage = this.store.createRecord('subscription-page', { id: SubscriptionCenterPages.Preferences });
    return this.preferencesPage;
  }

  updateTopicsTask = task(
    { drop: true },
    async (topics: SubscriptionTopicModel[], page: SubscriptionPageModel | null) => {
      if (page?.hasDirtyAttributes) {
        // trim title and subtitle before saving
        page.trimHeader();
        await page.save();
      }

      return await all(
        topics
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          .filter((topic) => topic.hasDirtyAttributes || topic.isDeleted)
          .map(async (topic) =>
            (topic.isDeleted as unknown as boolean) ? await topic.destroyRecord() : await topic.save(),
          ),
      );
    },
  );

  updateTopicOrderTask = task({ drop: true }, async (topicOrderByName: string[]) => {
    try {
      // GET updated/created topics
      await this.fetchTopics();

      let topicOrderByID = topicOrderByName.map((topicName) =>
        Number(this.topics.find((item) => item.name === topicName)?.id),
      );
      await this.api.put('subscription_topic_order', { data: { topic_order: topicOrderByID } });
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  deleteLanguages = task({ drop: true }, async (languages: SubscriptionLanguageModel[]) => {
    try {
      await this.api.delete('subscription_languages', { data: { languages: languages.map((l) => l.id) } });
      languages.forEach((language) => this.store.unloadRecord(language));
      this.fetchLanguages();

      this.alerts.success(
        'subscriptionCenter.languages.deleted',
        `${formatNumberAndPluralize([languages.length, 'language'])} deleted`,
      );
      this.analytics.track('subscription_center', 'edit_languages_submitted', {
        editionType: 'delete',
      });
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  enableDisableLanguages = task({ drop: true }, async (languages: SubscriptionLanguageModel[], enabled: boolean) => {
    try {
      let languagesToChange = languages.filter((lang) => lang.enabled !== enabled);
      let updatedLanguages = await this.api.put('subscription_languages', {
        data: {
          subscription_languages: languages.map((l) => {
            return { id: l.id, enabled };
          }),
        },
      });
      this.store.pushPayload(updatedLanguages);
      this.fetchLanguages();

      let successMessage = `${languagesToChange.length} ${pluralize(
        languagesToChange.length,
        'language is',
        'languages are',
      )} ${enabled ? 'visible' : 'hidden'} to your audience`;
      this.alerts.success('subscriptionCenter.languages.madeLive', successMessage);
      this.analytics.track('subscription_center', 'edit_languages_submitted', {
        editionType: enabled ? 'make live' : 'make not live',
      });
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  applyPreferences = task(async (bulkPrefUpdate: BulkPreferenceUpdateParams) => {
    try {
      await this.api.put('subscription_topic_messages/update', {
        data: {
          updates: {
            campaign_ids: bulkPrefUpdate.campaignIDs,
            newsletter_ids: bulkPrefUpdate.newsletterIDs,
          },
          subscription_topic_id: bulkPrefUpdate?.topicID,
          send_to_unsubscribed: bulkPrefUpdate?.sendToUnsub,
        },
      });

      this.fetchPendingMessageLists.perform({});
      this.fetchCompletedMessageLists.perform({});
      this.fetchAllMessageLists.perform({});

      let updatedMessageCount = bulkPrefUpdate.campaignIDs.length + bulkPrefUpdate.newsletterIDs.length;
      this.alerts.success(
        'subscriptionCenter.bulkPrefUpdate.apply',
        `${formatNumberAndPluralize([updatedMessageCount, 'item'])} added to ${bulkPrefUpdate.preferenceName}`,
      );
      this.analytics.track('subscription_center', 'set_bulk_preferences_submitted');
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  // called when switching workspaces:
  unload() {
    this.initialized = false;
    this.allTopics = [];
    this.allLanguages = [];
    this.pendingMessages = { pagination: { page: 0, size: 0, total: 0 } };
    this.completedMessages = { pagination: { page: 0, size: 0, total: 0 } };
    this.allMessages = { pagination: { page: 0, size: 0, total: 0 } };
    this.pages = [];
    this.preferencesPage = null;

    this.initializeTask.cancelAll({ resetState: true });
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'subscription-center': SubscriptionCenterService;
  }
}
